import moment from 'moment'
import { Fragment, useEffect, useState } from 'react'
import { IoCloudOfflineOutline } from 'react-icons/io5'

import { Menu, Transition } from '@headlessui/react'
import {
  ArrowRightOnRectangleIcon,
  CheckCircleIcon,
  Cog6ToothIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline'

import useServer from '../../api/useServerMain'
import { useAppSelector } from '../../store/hooks'
import LoadingSpin from '../misc/LoadingSpin'
import LogoComponent from '../misc/LogoComponent'
import Modal from '../modals/GenericModal'
import PasswordModal from './PasswordModal'
import UserModal from './UserModal'
import userDefaultAvatar from '@/assets/user-default.jpg'
import { join } from 'path'
import { readFileSync } from 'fs'

const Navbar = () => {
  const { logOut, isFetching } = useServer()
  const { user, business } = useAppSelector((state) => state.init)

  const [userModal, setUserModal] = useState(false)
  const [passwModal, setPasswModal] = useState(false)

  //Conexion listener -----------------------------------------------------------------
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  useEffect(() => {
    function onlineHandler() {
      setIsOnline(true)
    }

    function offlineHandler() {
      setIsOnline(false)
    }

    window.addEventListener('online', onlineHandler)
    window.addEventListener('offline', offlineHandler)

    return () => {
      window.removeEventListener('online', onlineHandler)
      window.removeEventListener('offline', offlineHandler)
    }
  }, [])

  //@ts-ignore
  const version = __PACKAGE_JSON_VERSION__ // Accede a la variable global

  //--------------------------------------------------------------------------------
  //Licence alert ---------------------------------------------------------
  let difference: number | undefined
  let licenceData = {
    color: 'green-700',
    text: 'Licencia activa',
    icon: CheckCircleIcon,
  }

  if (business?.licenceUntil) {
    difference = moment().diff(moment(business?.licenceUntil), 'hours')
    if (difference >= 0) {
      licenceData = {
        color: 'red-600',
        text: 'Su licencia expiró',
        icon: ExclamationTriangleIcon,
      }
    } else if (Math.abs(difference) < 72) {
      licenceData = {
        color: 'yellow-500',
        text: `Su licencia expira el ${moment(
          business?.licenceUntil ?? ''
        ).format('DD/MM/YYYY hh:mm A')}`,
        icon: ExclamationTriangleIcon,
      }
    }
  }
  //------------------------------------------------------------------------
  return (
    <>
      <div
        className={`fixed top-0 z-40 flex h-16 w-full flex-shrink-0 bg-${
          business?.subscriptionPlan.code !== 'FREE' &&
          difference &&
          difference >= 0
            ? 'red'
            : 'gray'
        }-50 shadow`}
      >
        <div className="flex flex-1 items-center justify-between gap-10 px-4">
          <div className="flex">
            <div className="h-8 w-8">
              <LogoComponent />
            </div>
            <div className={'ml-3 flex flex-col justify-center'}>
              <h4 className="font-semibold">Tecopos</h4>
              <p className={'ml-1 text-xs italic'}>v{version}</p>
            </div>
          </div>

          {!navigator.onLine && (
            <div className="flex flex-grow items-center justify-center gap-2">
              <IoCloudOfflineOutline className="font-semibold text-red-500" />
              <h5 className="font-semibold text-red-500">Sin conexión</h5>
            </div>
          )}

          <div className="inline-flex gap-5">
            {business?.subscriptionPlan.code !== 'FREE' && (
              <div className="inline-flex flex-shrink-0 items-center justify-end gap-1">
                {
                  <licenceData.icon
                    className={`h-5 text-${licenceData.color}`}
                  />
                }
                <p className={`text-${licenceData.color} text-sm`}>
                  {licenceData.text}
                </p>
              </div>
            )}

            <div className="ml-4 flex flex-shrink-0 items-center md:ml-6">
              {/* Profile dropdown */}
              <Menu as="div" className="relative ml-3">
                <div>
                  <Menu.Button className="flex max-w-xs items-center rounded-full bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
                    <span className="sr-only">Open user menu</span>
                    <img
                      className="h-8 w-8 rounded-full"
                      src={user?.avatar?.src ?? userDefaultAvatar}
                      alt=""
                    />
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <Menu.Item>
                      <div>
                        <div className="relative flex items-center gap-2 rounded-lg bg-white px-2 py-2">
                          <div className="flex-shrink-0">
                            <img
                              className="h-16 w-16 rounded-full"
                              src={user?.avatar?.src ?? userDefaultAvatar}
                              alt=""
                            />
                          </div>
                          <div className="min-w-0 flex-1">
                            <a href="#" className="focus:outline-none">
                              <span
                                className="absolute inset-0"
                                aria-hidden="true"
                              />
                              <p className="text-sm font-medium text-gray-900">
                                {user?.displayName}
                              </p>
                              <p className="flex flex-col text-xs text-gray-500">
                                {user?.roles?.map((item, idx) => (
                                  <span key={idx}>{item.name}</span>
                                ))}
                              </p>
                            </a>
                          </div>
                        </div>
                        <button
                          className="flex w-full items-center justify-start gap-1 px-4 py-2 text-sm font-medium text-gray-700 shadow-inner"
                          onClick={(e) => {
                            //e.preventDefault();
                            setUserModal(true)
                          }}
                        >
                          <Cog6ToothIcon className="h-5" />
                          Configurar mi cuenta
                        </button>
                        <button
                          className="flex w-full items-center justify-start gap-1 px-4 py-2 text-sm font-medium text-gray-700 shadow-inner"
                          onClick={(e) => {
                            //e.preventDefault();
                            setPasswModal(true)
                          }}
                        >
                          <LockClosedIcon className="h-5" />
                          Cambiar contraseña
                        </button>
                        <button
                          className="flex w-full items-center justify-start gap-1 px-4 py-2 text-sm font-medium text-gray-700 shadow-inner"
                          onClick={(e) => {
                            e.preventDefault()
                            logOut()
                          }}
                        >
                          {isFetching ? (
                            <LoadingSpin color="gray-800" />
                          ) : (
                            <ArrowRightOnRectangleIcon className="h-5" />
                          )}
                          Salir
                        </button>
                      </div>
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
      </div>

      {userModal && (
        <Modal state={userModal} close={() => setUserModal(false)}>
          <UserModal closeModal={() => setUserModal(false)} />
        </Modal>
      )}

      {passwModal && (
        <Modal state={passwModal} close={() => setPasswModal(false)}>
          <PasswordModal closeModal={() => setPasswModal(false)} />
        </Modal>
      )}
    </>
  )
}

export default Navbar
